import { Icon } from '@iconify/react';
import Box from '@mui/material/Box';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useNavigate } from 'react-router-dom';
import type { DashTable, KpiUnit } from '../dashboards.api';
import { formatValue } from '../dashboards.utils';
import { ChartFrame } from './ChartCard';
import MaybeLink, { useCanOpen } from './MaybeLink';

const NUMERIC: (KpiUnit | null)[] = ['COUNT', 'INR', 'HOURS', 'DAYS', 'PERCENT'];

function cellText(value: unknown, unit: KpiUnit | null): string {
  if (value === null || value === undefined || value === '') return '—';
  if (unit) return formatValue(value, unit);
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  // ISO dates from the API: show the date part only (dd/mm/yyyy).
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}(T|$)/.test(value)) {
    const [y, m, d] = value.slice(0, 10).split('-');
    return `${d}/${m}/${y}`;
  }
  return String(value);
}

/** A short list from the dashboard (top items, what is waiting); a row opens its screen when the user can. */
export default function DataTableCard({ table }: { table: DashTable }) {
  const navigate = useNavigate();
  const canOpen = useCanOpen();
  const rows = table.rows ?? [];
  const columns = table.columns ?? [];
  const cell = { px: 1, py: 0.75, borderBottom: '1px solid', borderColor: 'grey.300', fontSize: '0.8125rem' } as const;

  const title = (
    <MaybeLink to={table.link}>
      {table.title}
      {table.link && <Icon icon='mdi:arrow-top-right' width={14} style={{ verticalAlign: 'middle', marginLeft: 4, opacity: 0.6 }} />}
    </MaybeLink>
  );

  return (
    <ChartFrame title={title}>
      {rows.length === 0 ? (
        <Stack alignItems='center' sx={{ py: 4, color: 'text.secondary' }} gap={1}>
          <Icon icon='mdi:check-all' width={24} />
          <Typography variant='body2'>{table.emptyText || 'Nothing to show'}</Typography>
        </Stack>
      ) : (
        <Box sx={{ overflowX: 'auto', maxHeight: 360, overflowY: 'auto' }}>
          <Box component='table' sx={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                {columns.map((c) => (
                  <Box
                    component='th'
                    key={c.key}
                    sx={{
                      ...cell,
                      position: 'sticky',
                      top: 0,
                      bgcolor: 'background.paper',
                      textAlign: NUMERIC.includes(c.unit) ? 'right' : 'left',
                      color: 'text.secondary',
                      fontWeight: 500,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {c.label}
                  </Box>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => {
                const link = typeof row._link === 'string' && canOpen(row._link) ? row._link : null;
                return (
                  <Box
                    component='tr'
                    key={i}
                    tabIndex={link ? 0 : undefined}
                    onClick={link ? () => navigate(link) : undefined}
                    onKeyDown={
                      link
                        ? (e) => {
                            if (e.key === 'Enter') navigate(link);
                          }
                        : undefined
                    }
                    sx={link ? { cursor: 'pointer', '&:hover, &:focus-visible': { bgcolor: 'action.hover', outline: 'none' } } : undefined}
                  >
                    {columns.map((c) => (
                      <Box
                        component='td'
                        key={c.key}
                        sx={{
                          ...cell,
                          textAlign: NUMERIC.includes(c.unit) ? 'right' : 'left',
                          fontVariantNumeric: NUMERIC.includes(c.unit) ? 'tabular-nums' : undefined,
                          whiteSpace: NUMERIC.includes(c.unit) ? 'nowrap' : undefined,
                        }}
                      >
                        {cellText(row[c.key], c.unit)}
                      </Box>
                    ))}
                  </Box>
                );
              })}
            </tbody>
          </Box>
        </Box>
      )}
    </ChartFrame>
  );
}

export function DataTableCardSkeleton() {
  return (
    <ChartFrame title={<Skeleton variant='text' width='40%' />}>
      <Stack gap={0.5}>
        {[0, 1, 2, 3, 4].map((i) => (
          <Skeleton key={i} variant='text' height={28} />
        ))}
      </Stack>
    </ChartFrame>
  );
}
